import { keyframes } from '@emotion/react';
import CollectionsIcon from '@mui/icons-material/Collections';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loading } from '@/core/components';
import { useAuth } from '@/core/hooks/useAuth';
import { getCompletedClaims } from '@/core/services/claim';
import type { Claim } from '@/core/types';
import { NftCard, NftDetailDialog } from './components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export function GalleryPage() {
  const { raceAlias } = useParams<{ raceAlias: string }>();
  const { user, loading: authLoading } = useAuth();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  const fetchClaims = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const completedClaims = await getCompletedClaims(user.uid);
      setClaims(completedClaims);
    } catch (err) {
      console.error('Failed to fetch claims:', err);
      setError('完走証の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchClaims();
    }
  }, [authLoading, user, fetchClaims]);

  const handleCardClick = (claim: Claim) => {
    setSelectedClaim(claim);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedClaim(null);
  };

  if (authLoading) {
    return <Loading />;
  }

  if (!user) {
    return (
      <Alert severity="warning" sx={{ animation: `${fadeInUp} 0.6s ease-out` }}>
        ログインが必要です
      </Alert>
    );
  }

  return (
    <Box>
      {/* タイトルエリア */}
      <Box
        sx={{
          textAlign: 'center',
          mt: { xs: 2, sm: 5 },
          mb: { xs: 3, sm: 5 },
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            letterSpacing: '0.2em',
            color: 'text.secondary',
            mb: 1,
            fontSize: '0.7rem',
          }}
        >
          YOUR COLLECTION
        </Typography>

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 800,
            background: gradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          ギャラリー
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            animation: `${fadeInUp} 0.6s ease-out 0.1s both`,
          }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Loading />
      ) : claims.length === 0 ? (
        /* 空状態 */
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            animation: `${fadeInUp} 0.6s ease-out 0.1s both`,
          }}
        >
          <CollectionsIcon
            sx={{
              fontSize: 64,
              color: 'text.disabled',
              mb: 2,
            }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            まだ完走証がありません
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: raceAlias ? 3 : 0 }}>
            大会に参加して完走証を受け取りましょう
          </Typography>
          {raceAlias && (
            <Button
              variant="outlined"
              href={`/${raceAlias}/claim`}
              sx={{
                position: 'relative',
                fontWeight: 600,
                border: 'none',
                background: theme.palette.background.paper,
                color: theme.palette.text.primary,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 'inherit',
                  padding: '2px',
                  background: gradient,
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  pointerEvents: 'none',
                },
                '&:hover': {
                  background: theme.palette.background.paper,
                },
              }}
            >
              完走証を受け取る
            </Button>
          )}
        </Box>
      ) : (
        /* カード一覧 */
        <Grid container spacing={2}>
          {claims.map((claim, index) => (
            <Grid
              key={claim.id}
              size={{ xs: 6, sm: 6, md: 4, lg: 4 }}
              sx={{
                animation: `${fadeInUp} 0.6s ease-out ${0.1 + index * 0.05}s both`,
              }}
            >
              <NftCard claim={claim} onClick={() => handleCardClick(claim)} />
            </Grid>
          ))}
        </Grid>
      )}

      <NftDetailDialog claim={selectedClaim} open={dialogOpen} onClose={handleDialogClose} />
    </Box>
  );
}
